import { Alert, Chip, Stack } from "@mui/material";
import { ValueItems } from "Components/Shared/ValuesBox/ValueItems";
import { ValuesBox } from "Components/Shared/ValuesBox/ValuesBox";
import {
  ComgateOnboardingStatus,
  type MerchantTerminalContractDto,
  MerchantTerminalContractType,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";

const COMPONENT_RESOURCES = Resources.Components.Merchant.TerminalContracts;

type Props = {
  contract: MerchantTerminalContractDto;
};

const COMGATE_STATUS_COLORS: Record<
  ComgateOnboardingStatus,
  "default" | "success" | "error" | "warning" | "info"
> = {
  [ComgateOnboardingStatus.REQUESTED]: "info",
  [ComgateOnboardingStatus.PROCESSING]: "info",
  [ComgateOnboardingStatus.WAITING_FOR_DOCUMENTS]: "warning",
  [ComgateOnboardingStatus.WAITING_FOR_AUTHORIZATION_PAYMENT]: "warning",
  [ComgateOnboardingStatus.ERRORS_IN_DOCUMENTS]: "error",
  [ComgateOnboardingStatus.ERROR_ACTION_PENDING]: "error",
  [ComgateOnboardingStatus.REJECTED]: "error",
  [ComgateOnboardingStatus.ASSOCIATION_REGISTERING]: "info",
  [ComgateOnboardingStatus.READY]: "success",
};

export const ContractDetail = ({
  contract: { type, ucbTerminalContractDetail, comgateTerminalContractDetail },
}: Props) => {
  const { t } = useResource();

  const ucbValues = [
    { label: t(COMPONENT_RESOURCES.Form.Fields.Type), value: type },
    {
      label: t(COMPONENT_RESOURCES.Form.Fields.GprsCount),
      value: ucbTerminalContractDetail?.gprsCount || 0,
    },
  ];

  const comgateValues = [
    { label: t(COMPONENT_RESOURCES.Form.Fields.Type), value: type },
    {
      label: t(COMPONENT_RESOURCES.Form.Fields.TerminalCount),
      value: comgateTerminalContractDetail?.terminalCount || 0,
    },
    {
      label: t(COMPONENT_RESOURCES.Form.Fields.SoftPosCount),
      value: comgateTerminalContractDetail?.softPosCount || 0,
    },
    {
      label: t(COMPONENT_RESOURCES.Form.Fields.Currencies),
      value: comgateTerminalContractDetail?.currencies || "",
    },
    {
      label: t(COMPONENT_RESOURCES.Form.Fields.Tariff),
      value: comgateTerminalContractDetail?.tariff || "",
    },
    {
      label: t(COMPONENT_RESOURCES.Form.Fields.PayoutIBAN),
      value: comgateTerminalContractDetail?.payoutIBAN || "",
    },
    {
      label: t(COMPONENT_RESOURCES.Form.Fields.PayoutBIC),
      value: comgateTerminalContractDetail?.payoutBIC || "",
    },
    {
      label: t(COMPONENT_RESOURCES.Form.Fields.PayoutCurrency),
      value: comgateTerminalContractDetail?.payoutCurrency || "",
    },
    {
      label: t(COMPONENT_RESOURCES.Form.Fields.PayoutAccountNumber),
      value: comgateTerminalContractDetail?.payoutAccountNumber || "",
    },
  ];

  const values =
    type === MerchantTerminalContractType.COMGATE ? comgateValues : ucbValues;

  const comgateStatus = comgateTerminalContractDetail?.comgateStatus;
  const comgateMID = comgateTerminalContractDetail?.comgateMID;
  const isComgate = type === MerchantTerminalContractType.COMGATE;
  const isSubmitted = !!comgateMID;

  return (
    <Stack gap={2}>
      <ValuesBox title={type}>
        <ValueItems values={values} />
      </ValuesBox>

      {isComgate && isSubmitted && comgateStatus && (
        <Alert
          severity={
            comgateStatus === ComgateOnboardingStatus.READY
              ? "success"
              : comgateStatus === ComgateOnboardingStatus.REJECTED
                ? "error"
                : "info"
          }
        >
          <Stack direction="row" alignItems="center" gap={1}>
            {t(COMPONENT_RESOURCES.Comgate.StatusLabel)}:{" "}
            <Chip
              label={t(
                COMPONENT_RESOURCES.Comgate.Statuses[
                  comgateStatus as keyof typeof COMPONENT_RESOURCES.Comgate.Statuses
                ] ?? comgateStatus,
              )}
              color={COMGATE_STATUS_COLORS[comgateStatus] ?? "default"}
              size="small"
            />
            {!!comgateMID && <> | MID: {comgateMID}</>}
          </Stack>
        </Alert>
      )}

      {isComgate && !isSubmitted && (
        <Alert severity="info">
          {t(COMPONENT_RESOURCES.Comgate.SubmitViaOrbitL)}
        </Alert>
      )}
    </Stack>
  );
};
